import { useState } from "react";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import { Button, Modal, TextInput } from "@bfc-software/pallet";

/**
 * Drivers sign in with a numeric PIN. AAA sets no length of its own; the
 * previous Drivers page asked for at least four digits, and a generated PIN is
 * six, so this accepts four to six.
 */
const PIN_MIN_LENGTH = 4;
const PIN_MAX_LENGTH = 6;

export type Driver = {
  id: number;
  user_id: string;
  user_name?: string;
  active: boolean;
  created_at: string;
};

function generatePin(): string {
  return String(Math.floor(100000 + Math.random() * 900000));
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, "").slice(0, PIN_MAX_LENGTH);
}

const isValidPin = (pin: string) => pin.length >= PIN_MIN_LENGTH;

/** The PIN is only ever shown here, once, right after it is set. */
function PinRevealPanel({
  userId,
  pin,
  body,
  onDone,
  t,
}: {
  userId: string;
  pin: string;
  body: string;
  onDone: () => void;
  t: TFunction;
}) {
  return (
    <div className="flex flex-col">
      <p className="mb-16 text-size-13 text-on-surface-variant">{body}</p>
      <div className="mt-4 mb-20 grid grid-cols-2 gap-12 rounded-8 bg-surface-container py-12 px-16">
        <div>
          <span className="block section-heading">
            {t("manage.drivers.table.userId")}
          </span>
          <span className="block text-size-16 text-on-surface">{userId}</span>
        </div>
        <div>
          <span className="block section-heading">
            {t("manage.drivers.pinLabel")}
          </span>
          <span className="block font-mono text-size-16 text-on-surface">
            {pin}
          </span>
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-8">
        <Button
          kind="text-only"
          variant="avatar-surface"
          text={t("manage.drivers.done")}
          onPress={onDone}
        />
      </div>
    </div>
  );
}

function PinField({
  value,
  onChange,
  label,
  t,
  autofocus,
}: {
  value: string;
  onChange: (pin: string) => void;
  label: string;
  t: TFunction;
  autofocus?: boolean;
}) {
  return (
    <div className="flex items-end gap-8">
      <TextInput
        className="flex-1"
        name="pin"
        label={label}
        placeholder={t("manage.drivers.pinPlaceholder")}
        value={value}
        onChange={(ev) => onChange(digitsOnly(ev.target.value))}
        isRequired={true}
        maxLength={PIN_MAX_LENGTH}
        autofocus={autofocus}
      />
      <Button
        kind="text-only"
        type="button"
        variant="secondary"
        text={t("manage.drivers.generate")}
        onPress={() => onChange(generatePin())}
      />
    </div>
  );
}

/** `Modal` never caps a tall body, so the cap is an inline `style` (see the Selectors modals). */
const BODY_STYLE = { maxHeight: "calc(100vh - 320px)" };

export function AddDriverModal({
  isOpen,
  onOpenChange,
  onCreate,
  isPending,
  t,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rejects when the create failed; the caller has already reported it. */
  onCreate: (userId: string, pin: string, name: string) => Promise<void>;
  isPending: boolean;
  t: TFunction;
}) {
  const [name, setName] = useState("");
  const [userId, setUserId] = useState("");
  const [pin, setPin] = useState("");
  const [created, setCreated] = useState<{
    userId: string;
    pin: string;
  } | null>(null);

  // Every dismissal routes through here so Escape, the backdrop and the buttons
  // all clear the form; otherwise a stale `created` reappears on the next open.
  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      setName("");
      setUserId("");
      setPin("");
      setCreated(null);
    }
  };
  const close = () => handleOpenChange(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedId = userId.trim();
    if (isPending || !trimmedId || !isValidPin(pin)) return;

    try {
      await onCreate(trimmedId, pin, name.trim());
      setCreated({ userId: trimmedId, pin });
    } catch {
      // Already reported by the caller; keep the form open to retry.
    }
  };

  return (
    <Modal
      control="external"
      data-test-role="add-driver-modal"
      isDismissable={!isPending}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      width="standard"
      header={
        <h2 className="mb-4 text-size-18 text-on-surface">
          {created
            ? t("manage.drivers.add.successTitle")
            : t("manage.drivers.add.title")}
        </h2>
      }
    >
      <div
        className="flex max-w-full flex-col overflow-y-auto"
        // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
        style={BODY_STYLE}
      >
        {created ? (
          <PinRevealPanel
            userId={created.userId}
            pin={created.pin}
            body={t("manage.drivers.add.successBody", {
              userId: created.userId,
            })}
            onDone={close}
            t={t}
          />
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-12">
            <p className="mb-16 text-size-13 text-on-surface-variant">
              {t("manage.drivers.add.intro")}
            </p>
            <TextInput
              name="user-id"
              label={t("manage.drivers.table.userId")}
              placeholder={t("manage.drivers.add.userIdPlaceholder")}
              value={userId}
              onChange={(ev) => setUserId(ev.target.value)}
              isRequired={true}
              autofocus={true}
            />
            <TextInput
              name="name"
              label={t("manage.drivers.add.nameLabel")}
              placeholder={t("manage.drivers.add.namePlaceholder")}
              value={name}
              onChange={(ev) => setName(ev.target.value)}
            />
            <PinField
              value={pin}
              onChange={setPin}
              label={t("manage.drivers.pinLabel")}
              t={t}
            />
            <div className="mt-4 flex justify-end gap-8">
              <Button
                kind="text-only"
                variant="avatar-surface"
                type="submit"
                text={
                  isPending
                    ? t("manage.drivers.add.submitting")
                    : t("manage.drivers.add.submit")
                }
                isDisabled={isPending || !userId.trim() || !isValidPin(pin)}
              />
              <Button
                kind="text-only"
                type="button"
                variant="tertiary"
                text={t("manage.drivers.cancel")}
                onPress={close}
                isDisabled={isPending}
              />
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}

export function ResetPinModal({
  driver,
  onOpenChange,
  onSubmit,
  isPending,
  t,
}: {
  driver: Driver | null;
  onOpenChange: (open: boolean) => void;
  /** Rejects when the reset failed; the caller has already reported it. */
  onSubmit: (pin: string) => Promise<void>;
  isPending: boolean;
  t: TFunction;
}) {
  const [pin, setPin] = useState("");
  const [resetPin, setResetPin] = useState<string | null>(null);

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      setPin("");
      setResetPin(null);
    }
  };
  const close = () => handleOpenChange(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isPending || !isValidPin(pin)) return;

    try {
      await onSubmit(pin);
      setResetPin(pin);
    } catch {
      // Already reported by the caller; keep the form open.
    }
  };

  return (
    <Modal
      control="external"
      data-test-role="reset-pin-modal"
      isDismissable={!isPending}
      isOpen={driver !== null}
      onOpenChange={handleOpenChange}
      width="standard"
      header={
        <h2 className="mb-4 text-size-18 text-on-surface">
          {driver && resetPin
            ? t("manage.drivers.resetPin.successTitle")
            : t("manage.drivers.resetPin.title")}
        </h2>
      }
    >
      <div
        className="flex max-w-full flex-col overflow-y-auto"
        // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
        style={BODY_STYLE}
      >
        {driver && resetPin ? (
          <PinRevealPanel
            userId={driver.user_id}
            pin={resetPin}
            body={t("manage.drivers.resetPin.successBody", {
              userId: driver.user_id,
            })}
            onDone={close}
            t={t}
          />
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-12">
            <p className="mb-16 text-size-13 text-on-surface-variant">
              {driver
                ? t("manage.drivers.resetPin.intro", { userId: driver.user_id })
                : ""}
            </p>
            <PinField
              value={pin}
              onChange={setPin}
              label={t("manage.drivers.resetPin.pinLabel")}
              t={t}
              autofocus={true}
            />
            <div className="mt-4 flex justify-end gap-8">
              <Button
                kind="text-only"
                variant="avatar-surface"
                type="submit"
                text={
                  isPending
                    ? t("manage.drivers.resetPin.submitting")
                    : t("manage.drivers.resetPin.submit")
                }
                isDisabled={isPending || !isValidPin(pin)}
              />
              <Button
                kind="text-only"
                type="button"
                variant="tertiary"
                text={t("manage.drivers.cancel")}
                onPress={close}
                isDisabled={isPending}
              />
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}

export function EditNameModal({
  driver,
  onOpenChange,
  onSubmit,
  isPending,
  t,
}: {
  driver: Driver | null;
  onOpenChange: (open: boolean) => void;
  /** Rejects when the update failed; the caller has already reported it. */
  onSubmit: (name: string) => Promise<void>;
  isPending: boolean;
  t: TFunction;
}) {
  // Seeded from the driver each time the modal opens: the modal stays mounted,
  // so it keys on the driver id rather than carrying one driver's draft to the
  // next.
  return (
    <EditNameForm
      key={driver?.id ?? "closed"}
      driver={driver}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      isPending={isPending}
      t={t}
    />
  );
}

function EditNameForm({
  driver,
  onOpenChange,
  onSubmit,
  isPending,
  t,
}: {
  driver: Driver | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string) => Promise<void>;
  isPending: boolean;
  t: TFunction;
}) {
  const [name, setName] = useState(driver?.user_name ?? "");
  const close = () => onOpenChange(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isPending) return;

    try {
      await onSubmit(name.trim());
      close();
    } catch {
      // Already reported by the caller; keep the form open.
    }
  };

  return (
    <Modal
      control="external"
      data-test-role="edit-name-modal"
      isDismissable={!isPending}
      isOpen={driver !== null}
      onOpenChange={onOpenChange}
      width="standard"
      header={
        <h2 className="mb-4 text-size-18 text-on-surface">
          {t("manage.drivers.editName.title")}
        </h2>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-12">
        <p className="mb-16 text-size-13 text-on-surface-variant">
          {driver
            ? t("manage.drivers.editName.intro", { userId: driver.user_id })
            : ""}
        </p>
        <TextInput
          name="name"
          label={t("manage.drivers.add.nameLabel")}
          placeholder={t("manage.drivers.add.namePlaceholder")}
          value={name}
          onChange={(ev) => setName(ev.target.value)}
          autofocus={true}
        />
        <div className="mt-4 flex justify-end gap-8">
          <Button
            kind="text-only"
            variant="avatar-surface"
            type="submit"
            text={
              isPending
                ? t("manage.drivers.editName.submitting")
                : t("manage.drivers.editName.submit")
            }
            isDisabled={isPending}
          />
          <Button
            kind="text-only"
            type="button"
            variant="tertiary"
            text={t("manage.drivers.cancel")}
            onPress={close}
            isDisabled={isPending}
          />
        </div>
      </form>
    </Modal>
  );
}
